import { stringify } from 'query-string';
import { useContext, useEffect, useRef, useState } from 'react';
import { AuthenticationContext } from '../authentication/AuthenticationContext';
import { ApiError, ApiRequestOptions, HttpMethod, isRequestAbortedErrorMessage, makeRequest } from './api';

type State<TResponseBody> = {
  response: TResponseBody | null;
  inProgress: boolean;
  error: ApiError | null;
};

export type ApiRequest<TRequestBody, TResponseBody> = {
  sendRequest: (params?: {
    queryStringParameters?: Record<string, string>;
    requestBody?: TRequestBody;
    onSuccess?: (response: TResponseBody, headers: Headers) => void;
    onFailure?: (error: ApiError) => void;
  }) => Promise<void>;
  response: TResponseBody | null;
  inProgress: boolean;
  error: ApiError | null;
  cancel: () => void;
  reset: () => void;
};

export const useApiRequest = <
  TResponseBody extends unknown = undefined, // eslint-disable-line @typescript-eslint/no-unnecessary-type-constraint
  TRequestBody extends unknown = undefined // eslint-disable-line @typescript-eslint/no-unnecessary-type-constraint
>(options: {
  httpMethod: HttpMethod;
  endpointUrl: string;
  unauthenticated?: boolean;
}): ApiRequest<TRequestBody, TResponseBody> => {
  const abortController = useRef<AbortController>();
  const authenticationContext = useContext(AuthenticationContext);

  const previousRequestWasCancelledDueToNewRequestBeingSent = useRef(false);

  useEffect(() => {
    abortController.current = new AbortController();
    return () => {
      if (abortController.current != null) {
        abortController.current.abort();
      }
    };
  }, []);

  const [state, setState] = useState<State<TResponseBody>>({ response: null, inProgress: false, error: null });

  const sendRequest = async (params?: {
    queryStringParameters?: Record<string, string>;
    requestBody?: TRequestBody;
    onSuccess?: (response: TResponseBody, headers: Headers) => void;
    onFailure?: (error: ApiError) => void;
  }): Promise<void> => {
    const queryStringParameters = params && params.queryStringParameters;
    const requestBody = params && params.requestBody;

    if (state.inProgress) {
      previousRequestWasCancelledDueToNewRequestBeingSent.current = true;
      cancelRequest();
    }

    setState({
      response: null,
      inProgress: true,
      error: null,
    });

    const abortSignal = abortController.current == null ? undefined : abortController.current.signal;

    let accessToken: string | undefined;
    try {
      accessToken = options.unauthenticated ? undefined : await authenticationContext.getAccessToken();
    } catch {
      setState(currentState => ({
        ...currentState,
        inProgress: false,
        error: { message: 'Could not fetch access token', stackTrace: null, userVisibleMessage: null },
      }));
      return Promise.reject('Could not fetch access token');
    }

    let url = options.endpointUrl;
    if (queryStringParameters) {
      url = `${url}?${stringify(queryStringParameters)}`;
    }

    const requestOptions: ApiRequestOptions<TRequestBody> = {
      httpMethod: options.httpMethod,
      endpointUrl: url,
      requestBody,
      accessToken,
    } as ApiRequestOptions<TRequestBody>;

    makeRequest<TResponseBody, TRequestBody>(requestOptions, abortSignal)
      .then(result => {
        if (result.success) {
          setState({ response: result.body, inProgress: false, error: null });
          if (params != null && params.onSuccess != null) {
            params.onSuccess(result.body, result.headers);
          }
        } else {
          if (
            isRequestAbortedErrorMessage(result.error.message) &&
            previousRequestWasCancelledDueToNewRequestBeingSent.current
          ) {
            // If the previous request was aborted due to a new request being sent then the promise for the previous request
            // will be rejected with a particular error message. In this case we just want to swallow the error, because it's
            // a side-effect of the intended action.
            return;
          }

          setState({ response: null, inProgress: false, error: result.error });
          if (params != null && params.onFailure != null) {
            params.onFailure(result.error);
          }
        }
      })
      .catch(error => {
        setState({
          response: null,
          inProgress: false,
          error: { message: error.message, stackTrace: null, userVisibleMessage: null },
        });
      });
  };

  const cancelRequest = () => {
    if (abortController.current != null) {
      abortController.current.abort();
      abortController.current = new AbortController();
    }
  };

  const reset = () => {
    cancelRequest();

    setState({
      response: null,
      inProgress: false,
      error: null,
    });
  };

  return {
    sendRequest,
    response: state.response,
    inProgress: state.inProgress,
    error: state.error,
    cancel: cancelRequest,
    reset,
  };
};
